import { Loader2, RotateCcw } from 'lucide-react';
import type { LiveSessionListItem } from '../../lib/liveLessonTypes';
import { Badge, Button, Tile, TileContent, TileHeader, TileTitle } from '../ui';

const LIVE_STATUS_LABEL: Record<LiveSessionListItem['liveStatus'], string> = {
  preparing: 'Vorbereitung',
  live: 'Aktiv',
  paused: 'Pausiert',
  ended: 'Beendet'
};

const LIVE_STATUS_VARIANT: Record<
  LiveSessionListItem['liveStatus'],
  'neutral' | 'success' | 'warning' | 'info'
> = {
  preparing: 'neutral',
  live: 'success',
  paused: 'warning',
  ended: 'info'
};

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
}

type LiveSessionHistoryListProps = {
  sessions: LiveSessionListItem[];
  loading: boolean;
  resumingId: number | null;
  onResume: (entry: LiveSessionListItem) => void;
};

export default function LiveSessionHistoryList({
  sessions,
  loading,
  resumingId,
  onResume
}: LiveSessionHistoryListProps) {
  return (
    <Tile>
      <TileHeader>
        <TileTitle>Frühere Sessions</TileTitle>
      </TileHeader>
      <TileContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Noch keine gespeicherten Live-Sessions. Nach „Stunde starten“ erscheinen sie hier.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {sessions.map((entry) => {
              const label = entry.classLabel || entry.title;
              const subtitle = entry.theme || 'Ohne Thema';
              const isResuming = resumingId === entry.teachingUnitId;
              return (
                <li
                  key={entry.teachingUnitId}
                  className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium text-gray-900 dark:text-gray-100">{label}</span>
                      <Badge variant={LIVE_STATUS_VARIANT[entry.liveStatus]}>
                        {LIVE_STATUS_LABEL[entry.liveStatus]}
                      </Badge>
                      {entry.draftObservationCount > 0 ? (
                        <Badge variant="warning">{entry.draftObservationCount} Entwürfe</Badge>
                      ) : null}
                    </div>
                    <p className="mt-0.5 truncate text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-500">
                      Zuletzt: {formatWhen(entry.updatedAt)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={entry.liveStatus === 'ended' ? 'primary' : 'outline'}
                    className="shrink-0"
                    disabled={isResuming}
                    onClick={() => onResume(entry)}
                  >
                    {isResuming ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : entry.liveStatus === 'ended' ? (
                      <RotateCcw className="h-4 w-4" />
                    ) : null}
                    {entry.liveStatus === 'ended' ? 'Wieder aktivieren' : 'Fortsetzen'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </TileContent>
    </Tile>
  );
}
